import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { siteUrl } from '../config'

const OG_IMAGE = `${siteUrl}/og-image.png`

const SUPPORTED_LANGS = ['en', 'nl'] as const
type Lang = (typeof SUPPORTED_LANGS)[number]

const OG_LOCALES: Record<Lang, string> = {
  en: 'en_GB',
  nl: 'nl_NL',
}

type SEOProps = {
  page: 'home' | 'jobs'
  path: string
  jsonLd?: object | object[]
  image?: string
}

export const SEO = ({ page, path, jsonLd, image = OG_IMAGE }: SEOProps) => {
  const { t, i18n } = useTranslation('seo')
  const lang = (
    SUPPORTED_LANGS.includes(i18n.resolvedLanguage as Lang)
      ? i18n.resolvedLanguage
      : 'en'
  ) as Lang

  const title = t(`${page}.title`)
  const description = t(`${page}.description`)
  const canonical = `${siteUrl}${path}`

  const ldArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : []

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />

      <link rel="canonical" href={canonical} />
      {SUPPORTED_LANGS.map((alt) => (
        <link
          key={alt}
          rel="alternate"
          hrefLang={alt}
          href={`${siteUrl}${path}?lng=${alt}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Peakfijn" />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {SUPPORTED_LANGS.filter((l) => l !== lang).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALES[l]} />
      ))}
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Peakfijn" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@Peakfijn" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Peakfijn" />

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  )
}
